import React, { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Plus } from "lucide-react";
import BottomNav from "../../components/BottomNav.tsx";
import { baseurly } from "../../utils/apis.ts";
import { digitalOceanImageUrl } from "../../utils/digitalOcean.ts";

// Interface for Workstations
interface WorkStationData {
  id: number;
  title: string;
}

const AddWorkstations: React.FC = () => {
  const { buildingId } = useParams<{ buildingId: string }>(); // Fetch building ID from URL
  const navigate = useNavigate();
  const [workStations, setWorkStations] = useState<WorkStationData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // Add a new workstation entry
  const handleAddWorkStation = () => {
    setWorkStations([...workStations, { id: Date.now(), title: "" }]);
  };

  // Update the title for a specific workstation
  const handleTitleChange = (id: number, title: string) => {
    setWorkStations((prevStations) =>
      prevStations.map((ws) => (ws.id === id ? { ...ws, title } : ws))
    );
  };

  // Save workstations to API and navigate after completion
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
  
    const validWorkstations = workStations.filter((ws) => ws.title.trim() !== "");
  
    if (validWorkstations.length === 0) {
      console.error("No valid workstations to submit.");
      return;
    }
  
    try {
      // 🔍 Debugging Logs
      console.log("Submitting workstations:", validWorkstations);
  
      const requests = validWorkstations.map(async (workStation) => {
        const payload = {
          building_id: Number(buildingId),
          work_station: workStation.title,
        };
  
        console.log("Sending data:", payload); //  Debugging log
  
        const response = await fetch(`${baseurly}api/work_station`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });
  
        if (!response.ok) {
          const errorResponse = await response.text();
          throw new Error(`Failed to post workstation: ${errorResponse}`);
        }
  
        console.log("Response received:", response);
      });
  
      // Wait for all requests to finish
      await Promise.all(requests);
  
      console.log("All workstations posted successfully.");
      navigate("/select-building");
    } catch (error) {
      console.error("Error posting workstations:", error);
    } finally {
      setLoading(false);
    }
  };
  

  return (
    <div className="min-h-screen bg-blue-900 pb-20 items-center">
      {/* Header */}
      
        <div className="p-4">
          <img
            src={digitalOceanImageUrl("new%20logos/Asset%204.png")}
            alt="KMC Logo"
            className="w-16 h-auto ml-auto h-24 w-27"
          />
        </div>
      <div className="p-4 flex justify-between items-center w-full max-w-5xl">
        <h1 className="text-white text-xl font-bold">Assign Workstations</h1>
      </div>

      {/* Workstation List */}
      <div className="bg-white h-full w-full p-6 rounded-t-3xl flex-1">
      <div className="px-6 mb-6 bg-white rounded-full p-6">
          <div className="bg-white rounded-xl border p-8 flex justify-between items-center">
            <div>
              <h2 className="text-blue-900 text-xl font-bold mb-2">
                Work Stations
              </h2>
              <p className="text-gray-600 text-sm max-w-xs">
                Carefully Add the Work Stations as per the Selected Building.
              </p>
            </div>
            <img
              src={digitalOceanImageUrl("products/kayoola_coach/images/orders-coach/carousel.png")}
              alt="Bus illustration"
              className="w-27 h-24"
            />
          </div>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="max-w-2xl mx-auto">
            <h2 className="text-blue-900 text-3xl font-bold mb-4">Workstations</h2>

            {/* Add Workstation Button */}
            <button
              type="button"
              onClick={handleAddWorkStation}
              className="bg-blue-900 text-white px-4 py-2 rounded-full hover:bg-blue-800 mb-4 flex items-center gap-2"
            >
              <Plus className="w-5 h-5" /> Add Workstation
            </button>

            {/* Workstation Inputs */}
            {workStations.map((workStation) => (
              <div key={workStation.id} className="w-full max-w-2xl mx-auto mb-4 bg-gray-100 p-4 rounded-lg">
                <input
                  value={workStation.title}
                  onChange={(e) => handleTitleChange(workStation.id, e.target.value)}
                  placeholder="Enter workstation title"
                  className="w-full px-2 py-1 rounded border border-gray-300 focus:outline-none focus:border-blue-500"
                />
              </div>
            ))}

            {/* Save Workstations Button */}
            <button
              type="submit"
              className="bg-blue-900 text-white p-3 rounded-full hover:bg-blue-800 mt-6 w-full"
              disabled={loading}
            >
              {loading ? "Saving..." : "Save Workstations"}
            </button>
          </div>
        </form>
      </div>
      <BottomNav showBack={true}/>
    </div>
  );
};

export default AddWorkstations;
